// Centralized API client and environment-aware fetch interceptor

export const API_BASE = 
  (typeof import.meta !== 'undefined' && (import.meta as any).env?.VITE_API_URL) || 
  (typeof window !== 'undefined' && (window.location.hostname.endsWith('pages.dev') || (!window.location.hostname.includes('localhost') && window.location.hostname !== '127.0.0.1'))
    ? 'https://gamevault-api.muhammadumar9989.workers.dev'
    : '');

/**
 * Configure global fetch interceptor so any fetch('/api/...') automatically:
 * 1. Points to Cloudflare Worker API on production / pages.dev
 * 2. Attaches Authorization: Bearer <gv_token> if logged in
 * 3. Sets credentials: 'include' for cross-site cookie support
 */
export function initFetchInterceptor() {
  if (typeof window === 'undefined') return;

  const originalFetch = window.fetch.bind(window);

  window.fetch = async (input: RequestInfo | URL, init?: RequestInit) => {
    let url: string;
    let requestInit: RequestInit = { ...init };

    if (typeof input === 'string') {
      url = input;
    } else if (input instanceof URL) {
      url = input.toString();
    } else {
      url = input.url;
      if (!requestInit.method) requestInit.method = input.method;
      if (!requestInit.headers) requestInit.headers = input.headers;
    }

    // Rewrite relative /api calls or pages.dev /api calls to the Worker API
    if (url.startsWith('/api')) {
      url = `${API_BASE}${url}`;
    } else if (url.includes('.pages.dev/api/')) {
      url = url.replace(/https?:\/\/[^/]+\/api\//, `${API_BASE}/api/`);
    }

    // Attach credentials
    if (!requestInit.credentials) {
      requestInit.credentials = 'include';
    }

    // Attach authorization bearer header if available in localStorage
    const token = localStorage.getItem('gv_token');
    const headers = new Headers(requestInit.headers || {});
    if (token && !headers.has('Authorization')) {
      headers.set('Authorization', `Bearer ${token}`);
    }
    requestInit.headers = headers;

    return originalFetch(url, requestInit);
  };
}

export async function fetchApi<T = any>(endpoint: string, options: RequestInit = {}): Promise<T> {
  const url = endpoint.startsWith('/api') ? `${API_BASE}${endpoint}` : endpoint;
  const headers = new Headers(options.headers || {});
  if (!headers.has('Content-Type') && !(options.body instanceof FormData)) {
    headers.set('Content-Type', 'application/json');
  }

  const token = typeof window !== 'undefined' ? localStorage.getItem('gv_token') : null;
  if (token && !headers.has('Authorization')) {
    headers.set('Authorization', `Bearer ${token}`);
  }

  const res = await fetch(url, {
    ...options,
    credentials: 'include',
    headers,
  });

  const data: any = await res.json().catch(() => ({ success: false, error: 'Invalid JSON response' }));
  return data as T;
}

export const api = {
  get: <T = any>(url: string) => fetchApi<T>(url, { method: 'GET' }),
  post: <T = any>(url: string, body?: any) => fetchApi<T>(url, {
    method: 'POST',
    body: body instanceof FormData ? body : JSON.stringify(body)
  }),
  patch: <T = any>(url: string, body?: any) => fetchApi<T>(url, {
    method: 'PATCH',
    body: JSON.stringify(body)
  }),
  delete: <T = any>(url: string) => fetchApi<T>(url, { method: 'DELETE' }),
};

